import React, { useId, useState } from 'react';
import SlidePanel from '../SlidePanel';
import InfoTip from '../InfoTip';
import { apiService } from '../../services/api';
import { useToast } from '../../contexts/ToastContext';

interface Props {
  open: boolean;
  quarterLabel: string;
  quarterId: string;
  projects: Array<{ project_key: string; project_name: string }>;
  /** Start with this project chosen (the page is filtered to one). */
  defaultProject: string | null;
  onClose: () => void;
  onAdded: () => void;
}

const FIELD = 'mt-1 block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100';

// Anyone signed in may add an idea. It lives in the plan until someone makes the Jira ticket for it.
export default function AddIdeaPanel({ open, quarterLabel, quarterId, projects, defaultProject, onClose, onAdded }: Props) {
  const uid = useId();
  const { showToast } = useToast();
  const [title, setTitle] = useState('');
  const [project, setProject] = useState('');
  const [component, setComponent] = useState('');
  const [section, setSection] = useState<'plan' | 'adhoc' | 'ideation'>('plan');
  const [saving, setSaving] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) return;
    setSaving(true);
    try {
      const r = await apiService.addPlanIdea({
        title: title.trim(),
        quarter: quarterId,
        section,
        project_key: (project || defaultProject) ?? null,
        component: component.trim() || null,
      });
      if (!r.success) throw new Error(r.error);
      showToast('success', 'Idea added');
      setTitle('');
      setComponent('');
      onAdded();
      onClose();
    } catch (err: any) {
      showToast('error', err?.response?.data?.error || err?.message || 'Could not add the idea');
    } finally {
      setSaving(false);
    }
  };

  return (
    <SlidePanel isOpen={open} onClose={onClose} title="Add an idea" widthPercent={32}>
      <form onSubmit={submit} className="space-y-4 p-5">
        <p className="text-sm text-gray-700">
          An idea waits in the plan for {quarterLabel} until someone makes the Jira ticket for it<InfoTip id="plan.idea" />. A leader, an admin or the project owner sets its priority.
        </p>
        <div>
          <label htmlFor={`${uid}-title`} className="text-sm font-medium text-gray-800">What is the idea?</label>
          <textarea id={`${uid}-title`} rows={3} maxLength={300} required value={title} onChange={(e) => setTitle(e.target.value)} className={FIELD} />
        </div>
        <div>
          <label htmlFor={`${uid}-project`} className="text-sm font-medium text-gray-800">Project</label>
          <select id={`${uid}-project`} value={project || defaultProject || ''} onChange={(e) => setProject(e.target.value)} className={FIELD}>
            <option value="">Not sure yet</option>
            {projects.map((p) => (
              <option key={p.project_key} value={p.project_key}>{p.project_name}</option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor={`${uid}-component`} className="text-sm font-medium text-gray-800">Component (optional)</label>
          <input id={`${uid}-component`} type="text" maxLength={200} value={component} onChange={(e) => setComponent(e.target.value)} className={FIELD} />
        </div>
        <div>
          <label htmlFor={`${uid}-section`} className="text-sm font-medium text-gray-800">Where does it belong?</label>
          <select id={`${uid}-section`} value={section} onChange={(e) => setSection(e.target.value as typeof section)} className={FIELD}>
            <option value="plan">The plan, under its project</option>
            <option value="adhoc">Ad hoc: an unplanned ask</option>
            <option value="ideation">Ideation: an ask from elsewhere</option>
          </select>
        </div>
        <div className="flex justify-end">
          <button type="submit" disabled={saving || !title.trim()} className="rounded-lg bg-blue-700 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-50 motion-reduce:transition-none">
            {saving ? 'Adding…' : 'Add idea'}
          </button>
        </div>
      </form>
    </SlidePanel>
  );
}
